// Follow-ups (D-163): a telecaller's own, as on her phone; owners, admins and managers see the team's, each person's
// overdue, today's and coming ones, and change any of them. Filtered by day and by kind (call, visit, meeting, send
// documents, collect payment), the filters kept in the address.
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import {
  type Bucket,
  BUCKETS,
  FOLLOW_UP_KINDS,
  FOLLOW_UPS_PAGE,
  type FollowUpKind,
  type FollowUpQuery,
  type FollowUpRow,
  onTimeShare,
  VISIT_RESULTS,
} from "../lib/crm";
import { fmtDateTime, fmtNumber, fmtPhone, isManagerRole } from "../lib/format";
import { useMember } from "../app/session";
import { Due, FollowUpActions, FollowUpModal, type FollowUpStart, KindBadge, LabelBadge } from "../app/crm";
import { Card, Empty, ErrorNote, Loading, Page, TaskSource, useLoad } from "../app/ui";

export function Today() {
  const { api, member, tz } = useMember();
  const manager = isManagerRole(member.role);
  const [params, setParams] = useSearchParams();
  const query: FollowUpQuery = {
    who: params.get("who") ?? (manager ? "all" : "me"),
    kind: (params.get("kind") ?? "") as FollowUpKind | "",
    due: (params.get("due") ?? "") as Bucket | "",
    state: params.get("state") === "done" ? "done" : "open",
    q: params.get("q") ?? "",
    page: Math.max(0, Number(params.get("page") ?? 0) || 0),
  };
  const key = params.toString();
  const { data, error, loading, reload } = useLoad(() => api.followUps(query), [api, key], true);
  const [search, setSearch] = useState(query.q ?? "");
  const [editing, setEditing] = useState<{ leadId: string; start: FollowUpStart } | null>(null);

  const set = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    if (!("page" in patch)) next.delete("page");
    setParams(next);
  };

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const open = query.state === "open";
  const openCount = data ? Object.values(data.buckets).reduce((a, b) => a + b, 0) : 0;
  const who = query.who ?? "all";
  const team = manager && who !== "me";
  const from = query.page * FOLLOW_UPS_PAGE;

  return (
    <Page
      title={manager ? "Follow-ups" : "My follow-ups"}
      sub={data ? (open ? `${fmtNumber(openCount)} open` : `${fmtNumber(total)} done in the last 7 days`) : undefined}
      actions={manager && (
        <div className="segmented" role="group" aria-label="Whose">
          <button className={who === "all" ? "on" : ""} onClick={() => set({ who: "all" })}>Everyone</button>
          <button className={who === "me" ? "on" : ""} onClick={() => set({ who: "me" })}>Mine</button>
        </div>
      )}
    >
      {manager && data && data.people.length > 0 && (
        <Card title="By person" flush>
          <div className="table-wrap">
            <table className="people">
              <thead>
                <tr>
                  <th>Person</th>
                  <th className="num">Overdue</th>
                  <th className="num">Today</th>
                  <th className="num">Coming up</th>
                  <th className="num">Visits and meetings</th>
                  <th className="num">Done today</th>
                  <th className="num">On time, 7 days</th>
                </tr>
              </thead>
              <tbody>
                {data.people.map((p) => {
                  const share = onTimeShare(p.done_week, p.on_time_week);
                  return (
                    <tr
                      key={p.id}
                      className={`clickable${who === p.id ? " picked" : ""}`}
                      onClick={() => set({ who: who === p.id ? "all" : p.id })}
                    >
                      <td>
                        <button
                          className="link"
                          onClick={(e) => {
                            e.stopPropagation();
                            set({ who: who === p.id ? "all" : p.id });
                          }}
                        >
                          {p.name}
                        </button>
                      </td>
                      <td className={`num${p.overdue > 0 ? " overdue" : ""}`}>{fmtNumber(p.overdue)}</td>
                      <td className="num">{fmtNumber(p.today)}</td>
                      <td className="num">{fmtNumber(p.upcoming)}</td>
                      <td className="num">{fmtNumber(p.visits)}</td>
                      <td className="num">{fmtNumber(p.done_today)}</td>
                      <td className="num">
                        {share === null ? <span className="faint">–</span> : `${share}%`}
                        {p.done_week > 0 && <span className="faint">{" of "}{fmtNumber(p.done_week)}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="follow-up-filters">
        <div className="segmented" role="group" aria-label="When">
          <button className={open && !query.due ? "on" : ""} onClick={() => set({ state: "", due: "" })}>
            All open{data ? ` ${fmtNumber(openCount)}` : ""}
          </button>
          {BUCKETS.map((b) => (
            <button
              key={b.bucket}
              className={`${open && query.due === b.bucket ? "on" : ""}${b.bucket === "overdue" ? " overdue-tab" : ""}`}
              onClick={() => set({ state: "", due: b.bucket })}
            >
              {b.label}
              {data ? ` ${fmtNumber(data.buckets[b.bucket])}` : ""}
            </button>
          ))}
          <button className={!open ? "on" : ""} onClick={() => set({ state: "done", due: "" })}>Done, 7 days</button>
        </div>
        <div className="chips" role="group" aria-label="What">
          <button className={`chip${!query.kind ? " on" : ""}`} onClick={() => set({ kind: "" })}>Everything</button>
          {FOLLOW_UP_KINDS.map((k) => (
            <button
              key={k.kind}
              className={`chip${query.kind === k.kind ? " on" : ""}`}
              onClick={() => set({ kind: query.kind === k.kind ? "" : k.kind })}
            >
              {k.plural}
              {data && data.kinds[k.kind] > 0 ? ` ${fmtNumber(data.kinds[k.kind])}` : ""}
            </button>
          ))}
        </div>
        <form
          className="filters"
          onSubmit={(e) => {
            e.preventDefault();
            set({ q: search.trim() });
          }}
        >
          <input
            type="search"
            aria-label="Search follow-ups"
            placeholder="Customer's name or number"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button>Search</button>
        </form>
      </div>

      <ErrorNote error={error} />
      <Card flush>
        {loading && !data && (
          <div style={{ padding: 16 }}>
            <Loading />
          </div>
        )}
        {data && rows.length === 0 && (
          <Empty>
            {open ? "Nothing here. Every follow-up is done or planned for another day." : "Nothing done yet."}
          </Empty>
        )}
        {rows.length > 0 && (
          <div className="table-wrap">
            <table className="follow-ups">
              <thead>
                <tr>
                  <th>{open ? "When" : "Done"}</th>
                  <th>What</th>
                  <th>Customer</th>
                  {team && <th>Who</th>}
                  <th>Why</th>
                  {open && <th />}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <FollowUpLine
                    key={r.id}
                    row={r}
                    tz={tz}
                    team={team}
                    me={member.member_id}
                    heading={open && !query.due && r.bucket !== rows[i - 1]?.bucket ? r.bucket : null}
                    onChange={() => void reload()}
                    onEdit={() =>
                      setEditing({
                        leadId: r.lead.id,
                        start: {
                          id: r.id,
                          type: r.type,
                          due_at: r.due_at,
                          due_has_time: r.due_has_time,
                          assigned_to: r.member?.id ?? null,
                          place: r.place,
                        },
                      })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {total > FOLLOW_UPS_PAGE && (
        <div className="row between pager">
          <span className="faint">
            {fmtNumber(from + 1)}–{fmtNumber(Math.min(from + FOLLOW_UPS_PAGE, total))} of {fmtNumber(total)}
          </span>
          <span className="row">
            <button disabled={query.page === 0} onClick={() => set({ page: String(query.page - 1) })}>Previous</button>
            <button disabled={from + FOLLOW_UPS_PAGE >= total} onClick={() => set({ page: String(query.page + 1) })}>
              Next
            </button>
          </span>
        </div>
      )}

      {editing && (
        <FollowUpModal
          title="Change the follow-up"
          leadId={editing.leadId}
          start={editing.start}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void reload();
          }}
        />
      )}
    </Page>
  );
}

function FollowUpLine(props: {
  row: FollowUpRow;
  tz: string;
  team: boolean;
  me: string;
  heading: Bucket | null;
  onChange: () => void;
  onEdit: () => void;
}) {
  const r = props.row;
  const open = r.status === "open";
  const columns = 4 + (props.team ? 1 : 0) + (open ? 1 : 0);
  return (
    <>
      {props.heading && (
        <tr className="group-row">
          <td colSpan={columns}>{BUCKETS.find((b) => b.bucket === props.heading)?.label}</td>
        </tr>
      )}
      <tr>
        <td className="nowrap f-when">
          {open ? <Due dueAt={r.due_at} hasTime={r.due_has_time} tz={props.tz} /> : (
            <>
              <div>{r.completed_at ? fmtDateTime(r.completed_at, props.tz) : "Done"}</div>
              {r.result && <div className="faint">{VISIT_RESULTS.find((v) => v.value === r.result)?.label}</div>}
            </>
          )}
        </td>
        <td className="f-what">
          <KindBadge type={r.type} />
          {r.place && <div className="faint">{r.place}</div>}
          {r.booked_by && r.booked_by.id !== r.member?.id && <div className="faint">Booked by {r.booked_by.name}</div>}
        </td>
        <td className="f-customer">
          <div className="row">
            <Link className="primary" to={`/leads/${r.lead.id}`}>{r.lead.name}</Link>
            <LabelBadge label={r.lead.label} />
          </div>
          <div className="faint">
            <a href={`tel:${r.lead.phone}`}>{fmtPhone(r.lead.phone)}</a>
            {r.lead.stage ? ` · ${r.lead.stage}` : ""}
            {r.lead.context ? ` · ${r.lead.context}` : ""}
          </div>
        </td>
        {props.team && (
          <td className="f-who">
            {r.member
              ? (r.member.id === props.me ? "You" : r.member.name)
              : <span className="badge danger">Nobody</span>}
          </td>
        )}
        <td className="f-why">
          <div>{r.brief || r.reason}</div>
          <div className="faint">
            <TaskSource source={r.source} rule={r.rule} />
            {r.snoozed_count > 1 && ` · moved ${r.snoozed_count} times`}
          </div>
        </td>
        {open && (
          <td className="actions-cell">
            <FollowUpActions followUp={r} onChange={props.onChange} onEdit={props.onEdit} compact />
          </td>
        )}
      </tr>
    </>
  );
}
